import React, {useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {LoggedInView} from "../common/LoggedInView";
import {apiClient} from "../../utils/ApiClient";
import CategoryType from "../../models/CategoryType";
import {VendorContact} from "../../models/VendorContact";
import {Tab} from "../../models/Tab";
import CommentModal from "./components/CommentModal";
import {getCurrentInquiry} from "../../store/reducers/InquiriesSlice";

const TABS: Tab[] = [
    { identifier: "partial", name: "Grupp 1 (0.1-13,5 LDM)" },
    { identifier: "full", name: "Grupp 2 (13,6 LDM)" },
];

interface RecommendedVendorsScreenProps {
}

export const RecommendedVendorsScreen: React.FC<RecommendedVendorsScreenProps> = () => {

    const {id} = useParams();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const inquiriesState = useAppSelector((state) => state.inquiries);

    const [activeTab, setActiveTab] = useState<Tab>(TABS[0]);
    const [reverse, setReverse] = useState<boolean>(false);
    const [noMeter, setNoMeter] = useState<boolean>(false);

    const [routeComment, setRouteComment] = useState<string | null>(null);

    const [partialTopLoadings, setPartialTopLoadings] = useState<any[]>([]);
    const [fullTopLoadings, setFullTopLoadings] = useState<any[]>([]);
    const [partialHits, setPartialHits] = useState<any[]>([]);
    const [fullHits, setFullHits] = useState<any[]>([]);

    const [serviceId, setServiceId] = useState<number | null>(null);

    const inquiry = inquiriesState?.currentInquiry;

    const routeStartCountryId: number | null = inquiry
        ? (reverse ? inquiry.destination_country_id : inquiry.start_country_id)
        : null;

    const routeDestinationCountryId: number | null = inquiry
        ? (reverse ? inquiry.start_country_id : inquiry.destination_country_id)
        : null;

    const [commentModal, setCommentModal] = useState<null | {
        startCountryId: number
        destinationCountryId: number
        initialValue: string | null
    }>(null);

    useEffect(() => {
        if (!!id) {
            fetchData();
        }
    }, [id, reverse]);

    useEffect(() => {
        if (!id) return;

        dispatch(getCurrentInquiry({ id: parseInt(id) }));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        if (!inquiriesState?.currentInquiry) return;
        if (inquiriesState.currentInquiry.id !== parseInt(id)) return;

        const nextServiceId = inquiriesState.currentInquiry.service_id ?? null;
        setServiceId(typeof nextServiceId === 'number' ? nextServiceId : null);
    }, [id, inquiriesState.currentInquiry]);

    const fetchData = async () => {
        try {
            const response = await apiClient.get(`suggest/vendorContact/${id}?reverse=${reverse}`);
            const data = response.data;

            const hasNoMeter = !!data['no_meter'];
            setNoMeter(hasNoMeter);

            setRouteComment(typeof data?.recommendation_comment === 'string' ? data.recommendation_comment : null);

            setPartialTopLoadings(Array.isArray(data?.below13_6?.top) ? data.below13_6.top : []);
            setPartialHits(Array.isArray(data?.below13_6?.hits) ? data.below13_6.hits : []);

            if (!hasNoMeter) {
                setFullTopLoadings(Array.isArray(data?.above13_6?.top) ? data.above13_6.top : []);
                setFullHits(Array.isArray(data?.above13_6?.hits) ? data.above13_6.hits : []);
            } else {
                setFullTopLoadings([]);
                setFullHits([]);
                setActiveTab(TABS[0]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const openCommentModal = (existingComment: string | null) => {
        if (!routeStartCountryId || !routeDestinationCountryId) {
            console.error('Missing route ids for comment modal', {
                routeStartCountryId,
                routeDestinationCountryId,
                serviceId,
                reverse,
                inquiryId: id,
            });
            return;
        }

        setCommentModal({
            startCountryId: routeStartCountryId,
            destinationCountryId: routeDestinationCountryId,
            initialValue: existingComment ?? null,
        });
    };

    const closeCommentModal = () => {
        setCommentModal(null);
    };


    const [selectedCheckboxes, setSelectedCheckboxes] = useState(() => {
        const storedCheckboxes = sessionStorage.getItem('selectedCheckboxes' + id);
        return storedCheckboxes ? JSON.parse(storedCheckboxes) : {};
    });

    useEffect(() => {
        sessionStorage.setItem('selectedCheckboxes' + id, JSON.stringify(selectedCheckboxes));
    }, [selectedCheckboxes]);

    const handleCheckboxChange = (contactId: number) => {
        setSelectedCheckboxes((prevState: any) => ({
            ...prevState,
            [contactId]: !prevState[contactId]
        }));
    };

    const handleSendEmail = () => {
        const selectedRecommendations = new Map<number, VendorContact>();

        for (const contactId in selectedCheckboxes) {
            if (selectedCheckboxes[contactId]) {
                [
                    ...(Array.isArray(partialTopLoadings) ? partialTopLoadings : []),
                    ...(Array.isArray(fullTopLoadings) ? fullTopLoadings : [])
                ].forEach((loading) => {
                    loading.contacts.forEach((contact: VendorContact) => {
                        if (contact.id === parseInt(contactId)) {
                            selectedRecommendations.set(contact.id, contact);
                        }
                    });
                });

                [...(Array.isArray(fullHits) ? fullHits : []), ...(Array.isArray(partialHits) ? partialHits : [])].forEach((hit) => {
                    if (hit.vendor_contact && hit.vendor_contact.id === parseInt(contactId)) {
                        selectedRecommendations.set(hit.vendor_contact.id, hit.vendor_contact);
                    }
                });
            }
        }

        const uniqueContacts = Array.from(selectedRecommendations.values());
        sessionStorage.setItem('selectedRecommendations', JSON.stringify(uniqueContacts));

        navigate(`/inquiries/${id}/recommended/send`);
    };

    const handleReverse = () => {
        setReverse(!reverse);
    }

    const regularVendorWidths = {
        name: '25%',
        email: '15%',
        phone: '10%',
        skype: '15%',
        whatsapp: '15%',
        orders: '5%',
        rating: '5%'
    };

    const topVendorWidths = {
        name: '25%',
        email: '15%',
        phone: '10%',
        skype: '15%',
        whatsapp: '15%',
        orders: '5%',
        rating: '5%'
    };

    const handleTabClick = (tab: Tab) => {
        setActiveTab(tab);
    };

    const removeVendorContact = async (contactId: number, startCountryId: number, destinationCountryId: number) => {
        const confirmation = window.confirm("Kas oled kindel, et soovid kontakti soovitustest eemaldada?");
        if (confirmation) {
            await apiClient.post(`vendors/none-important/contacts/${contactId}/toggleRoute?inquiry=${id}&reverse=${reverse}`, {
                start_country_id: startCountryId,
                destination_country_id: destinationCountryId,
            });
            fetchData();
        }
    };


    const renderRouteComment = () => {
        return (
            <div style={{backgroundColor: '#f5f5f5', padding: '12px', marginBottom: '16px', borderRadius: '6px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h2 style={{margin: 0}}>Marsruudi kommentaar</h2>
                    <button
                        type="button"
                        onClick={() => openCommentModal(routeComment)}
                        disabled={!serviceId || !routeStartCountryId || !routeDestinationCountryId}
                        style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '4px',
                            border: '1px solid #c9c9c9',
                            backgroundColor: '#ffffff',
                            color: '#222',
                            lineHeight: 1.2,
                            cursor: (!serviceId || !routeStartCountryId || !routeDestinationCountryId) ? 'not-allowed' : 'pointer',
                            opacity: (!serviceId || !routeStartCountryId || !routeDestinationCountryId) ? 0.6 : 1,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {routeComment ? 'Muuda kommentaari' : 'Lisa kommentaar'}
                    </button>
                </div>

                {routeComment && (
                    <div
                        style={{
                            marginTop: '10px',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#fbfbfb',
                            border: '1px solid #e6e6e6',
                            fontSize: '12px',
                            color: '#444',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.35,
                        }}
                    >
                        {routeComment}
                    </div>
                )}
            </div>
        );
    };

    const renderTabContent = () => {
        if (noMeter) {
            return (
                <div style={{marginBottom: '20px'}}>
                    {renderRouteComment()}
                    <h2>Top Kontaktid <small style={{fontSize:'12px'}}>(Vedude arv 4a jooksul)</small></h2><br />
                    <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                        {partialTopLoadings.map((loading, index) => {
                            if ((loading.contacts ?? []).length > 0) {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading.vendor.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading.vendor.name}
                                                </Link>
                                                {' '}({loading.total_orders})
                                                {loading.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.rating}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(loading.contacts ?? []).map((contact: VendorContact, contactIndex: number) => (
                                                <tr key={contactIndex}>
                                                    <td style={{ width: topVendorWidths.name }}>
                                                        <Link to={`/vendors/${contact.vendor_id}/contacts/${contact.id}/modify`}>
                                                            {contact.name}
                                                        </Link>
                                                    </td>
                                                    <td style={{width: topVendorWidths.email}}>
                                                        <a href={`mailto:${contact.emails}`}>{contact.emails}</a>
                                                    </td>
                                                    <td style={{width: topVendorWidths.phone}}>{contact.phone}</td>
                                                    <td style={{ width: topVendorWidths.skype }}>
                                                        {contact.skype && (
                                                            <a href={`skype:${contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                                {contact.skype}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{ width: topVendorWidths.whatsapp }}>
                                                        {contact.whatsapp && (
                                                            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                                {contact.whatsapp}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{width: topVendorWidths.orders}}>{contact.orders_count}</td>
                                                    <td style={{width: regularVendorWidths.rating}}>
                                                        <Link to={`/vendors/${loading.vendor.id}/contacts/${contact.id}/ratings`}>
                                                            {contact?.rating?.toFixed(2) || '0.00'}
                                                        </Link>
                                                        <a
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                removeVendorContact(
                                                                    contact.id,
                                                                    loading.start_country_id,
                                                                    loading.destination_country_id
                                                                );
                                                            }}
                                                            style={{ color: 'red', textDecoration: 'none', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '10px' }}
                                                        >
                                                            ✖
                                                        </a>
                                                    </td>
                                                    <td style={{width: '50px'}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCheckboxes[contact.id] || false}
                                                            onChange={() => handleCheckboxChange(contact.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                    {partialHits?.length > 0 &&
                    <>
                        <h2>⭐ Hitihoiatused</h2>
                        <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                            {partialHits?.map((loading, index) => {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading?.vendor_contact?.vendor?.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading?.vendor_contact?.vendor?.name}
                                                </Link>
                                                {loading?.vendor_contact?.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.orders}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td style={{ width: topVendorWidths.name }}>
                                                    <Link to={`/vendors/${loading?.vendor_contact?.vendor_id}/contacts/${loading?.vendor_contact?.id}/modify`}>
                                                        {loading?.vendor_contact?.name}
                                                    </Link>
                                                </td>
                                                <td style={{width: topVendorWidths.email}}>
                                                    <a href={`mailto:${loading?.vendor_contact?.emails}`}>{loading?.vendor_contact?.emails}</a>
                                                </td>
                                                <td style={{width: topVendorWidths.phone}}>{loading?.vendor_contact?.phone}</td>
                                                <td style={{ width: topVendorWidths.skype }}>
                                                    {loading?.vendor_contact?.skype && (
                                                        <a href={`skype:${loading.vendor_contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.skype}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{ width: topVendorWidths.whatsapp }}>
                                                    {loading?.vendor_contact?.whatsapp && (
                                                        <a href={`https://wa.me/${loading.vendor_contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.whatsapp}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{width: topVendorWidths.orders}}>{loading?.vendor_contact?.orders_count}</td>
                                                <td style={{width: topVendorWidths.orders}}>
                                                    {loading?.vendor_contact?.rating ?? 0}
                                                    <a
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            removeVendorContact(
                                                                loading?.vendor_contact?.id,
                                                                loading?.start_country_id,
                                                                loading?.destination_country_id
                                                            );
                                                        }}
                                                        style={{ color: 'red', textDecoration: 'none', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '10px' }}
                                                    >
                                                        ✖
                                                    </a>
                                                </td>
                                                <td style={{width: '50px'}}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCheckboxes[loading?.vendor_contact?.id] || false}
                                                        onChange={() => handleCheckboxChange(loading?.vendor_contact?.id)}
                                                    />
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                    }
                </div>
            );
        }

        if (activeTab.identifier === "partial") {
            return (
                <div style={{marginBottom: '20px'}}>
                    {renderRouteComment()}
                    {!noMeter && <h1>Grupp 1 (0.1-13,5 LDM)</h1>}
                    <h2>Top Kontaktid <small style={{fontSize:'12px'}}>(Vedude arv 4a jooksul)</small></h2>
                    <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                        {partialTopLoadings.map((loading, index) => {
                            if ((loading.contacts ?? []).length > 0) {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading.vendor.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading.vendor.name}
                                                </Link>
                                                {' '}({loading.total_orders})
                                                {loading.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.rating}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(loading.contacts ?? []).map((contact: VendorContact, contactIndex: number) => (
                                                <tr key={contactIndex}>
                                                    <td style={{ width: topVendorWidths.name }}>
                                                        <Link to={`/vendors/${contact.vendor_id}/contacts/${contact.id}/modify`}>
                                                            {contact.name}
                                                        </Link>
                                                    </td>
                                                    <td style={{width: topVendorWidths.email}}><a
                                                        href={`mailto:${contact.emails}`}>{contact.emails}</a></td>
                                                    <td style={{width: topVendorWidths.phone}}>{contact.phone}</td>
                                                    <td style={{ width: topVendorWidths.skype }}>
                                                        <a href={`skype:${contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                            {contact.skype}
                                                        </a>
                                                    </td>
                                                    <td style={{ width: topVendorWidths.whatsapp }}>
                                                        <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                            {contact.whatsapp}
                                                        </a>
                                                    </td>
                                                    <td style={{width: topVendorWidths.orders}}>{contact.orders_count}</td>
                                                    <td style={{width: regularVendorWidths.rating}}>
                                                        <Link
                                                            to={`/vendors/${loading.vendor.id}/contacts/${contact.id}/ratings`}>{contact?.rating?.toFixed(2) || '0.00'}</Link>
                                                        <a
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                removeVendorContact(
                                                                    contact.id,
                                                                    loading.start_country_id,
                                                                    loading.destination_country_id
                                                                );
                                                            }}
                                                            style={{
                                                                color: 'red',
                                                                textDecoration: 'none',
                                                                cursor: 'pointer',
                                                                fontSize: '0.8rem',
                                                                marginLeft: '10px',
                                                            }}
                                                        >
                                                            ✖
                                                        </a>
                                                    </td>
                                                    <td style={{width: '50px'}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCheckboxes[contact.id] || false}
                                                            onChange={() => handleCheckboxChange(contact.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                    {partialHits?.length > 0 &&
                    <>
                        <h2>⭐ Hitihoiatused</h2>
                        <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                            {partialHits?.map((loading, index) => {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading?.vendor_contact?.vendor?.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading?.vendor_contact?.vendor?.name}
                                                </Link>
                                                {loading?.vendor_contact?.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.orders}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td style={{ width: topVendorWidths.name }}>
                                                    <Link to={`/vendors/${loading?.vendor_contact?.vendor_id}/contacts/${loading?.vendor_contact?.id}/modify`}>
                                                        {loading?.vendor_contact?.name}
                                                    </Link>
                                                </td>
                                                <td style={{width: topVendorWidths.email}}>
                                                    <a href={`mailto:${loading?.vendor_contact?.emails}`}>{loading?.vendor_contact?.emails}</a>
                                                </td>
                                                <td style={{width: topVendorWidths.phone}}>{loading?.vendor_contact?.phone}</td>
                                                <td style={{ width: topVendorWidths.skype }}>
                                                    {loading?.vendor_contact?.skype && (
                                                        <a href={`skype:${loading.vendor_contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.skype}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{ width: topVendorWidths.whatsapp }}>
                                                    {loading?.vendor_contact?.whatsapp && (
                                                        <a href={`https://wa.me/${loading.vendor_contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.whatsapp}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{width: topVendorWidths.orders}}>{loading?.vendor_contact?.orders_count}</td>
                                                <td style={{width: topVendorWidths.orders}}>
                                                    {loading?.vendor_contact?.rating ?? 0}
                                                    <a
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            removeVendorContact(
                                                                loading?.vendor_contact?.id,
                                                                loading?.start_country_id,
                                                                loading?.destination_country_id
                                                            );
                                                        }}
                                                        style={{ color: 'red', textDecoration: 'none', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '10px' }}
                                                    >
                                                        ✖
                                                    </a>
                                                </td>
                                                <td style={{width: '50px'}}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCheckboxes[loading?.vendor_contact?.id] || false}
                                                        onChange={() => handleCheckboxChange(loading?.vendor_contact?.id)}
                                                    />
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                    }
                </div>
            );
        } else if (activeTab.identifier === "full") {
            return (
                <div style={{marginBottom: '20px'}}>
                    {renderRouteComment()}
                    {!noMeter && <h1>Grupp 2 (13,6 LDM)</h1>}
                    <h2>Top Kontaktid <small style={{fontSize:'12px'}}>(Vedude arv 4a jooksul)</small></h2>
                    <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                        {fullTopLoadings.map((loading, index) => {
                            if ((loading.contacts ?? []).length > 0) {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading.vendor.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading.vendor.name}
                                                </Link>
                                                {' '}({loading.total_orders})
                                                {loading.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.rating}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(loading.contacts ?? []).map((contact: VendorContact, contactIndex: number) => (
                                                <tr key={contactIndex}>
                                                    <td style={{ width: topVendorWidths.name }}>
                                                        <Link to={`/vendors/${contact.vendor_id}/contacts/${contact.id}/modify`}>
                                                            {contact.name}
                                                        </Link>
                                                    </td>
                                                    <td style={{width: topVendorWidths.email}}><a
                                                        href={`mailto:${contact.emails}`}>{contact.emails}</a></td>
                                                    <td style={{width: topVendorWidths.phone}}>{contact.phone}</td>
                                                    <td style={{ width: topVendorWidths.skype }}>
                                                        {contact.skype && (
                                                            <a href={`skype:${contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                                {contact.skype}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{ width: topVendorWidths.whatsapp }}>
                                                        {contact.whatsapp && (
                                                            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                                {contact.whatsapp}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td style={{width: topVendorWidths.orders}}>{contact.orders_count}</td>
                                                    <td style={{width: regularVendorWidths.rating}}>
                                                        <Link
                                                            to={`/vendors/${loading.vendor.id}/contacts/${contact.id}/ratings`}>{contact?.rating?.toFixed(2) || '0'}</Link>
                                                        <a
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                removeVendorContact(
                                                                    contact.id,
                                                                    loading?.start_country_id,
                                                                    loading?.destination_country_id
                                                                );
                                                            }}
                                                            style={{
                                                                color: 'red',
                                                                textDecoration: 'none',
                                                                cursor: 'pointer',
                                                                fontSize: '0.8rem',
                                                                marginLeft: '10px',
                                                            }}
                                                        >
                                                            ✖
                                                        </a>
                                                    </td>
                                                    <td style={{width: '50px'}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCheckboxes[contact.id] || false}
                                                            onChange={() => handleCheckboxChange(contact.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                    {fullHits?.length > 0 &&
                    <>
                        <h2>⭐ Hitihoiatused</h2>
                        <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: '20px'}}>
                            {fullHits?.map((loading, index) => {
                                return (
                                    <div key={index}>
                                        <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <span>
                                                <Link
                                                    to={`/vendors/${loading?.vendor_contact?.vendor?.id}/modify`}
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    {loading?.vendor_contact?.vendor?.name}
                                                </Link>
                                                {loading?.vendor_contact?.vendor?.wca === 1 && <span style={{color: 'orange', marginLeft: '6px'}}>WCA</span>}
                                            </span>
                                        </h3>
                                        <table className="data">
                                            <thead>
                                            <tr>
                                                <th style={{width: topVendorWidths.name}}>Kontakt</th>
                                                <th style={{width: topVendorWidths.email}}>Email</th>
                                                <th style={{width: topVendorWidths.phone}}>Telefon</th>
                                                <th style={{width: topVendorWidths.skype}}>Teams</th>
                                                <th style={{width: topVendorWidths.whatsapp}}>Whatsapp</th>
                                                <th style={{width: topVendorWidths.orders}}>Vedusid</th>
                                                <th style={{width: topVendorWidths.rating}}>Hinnang</th>
                                                <th></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td style={{ width: topVendorWidths.name }}>
                                                    <Link to={`/vendors/${loading?.vendor_contact?.vendor_id}/contacts/${loading?.vendor_contact?.id}/modify`}>
                                                        {loading?.vendor_contact?.name}
                                                    </Link>
                                                </td>
                                                <td style={{width: topVendorWidths.email}}>
                                                    <a href={`mailto:${loading?.vendor_contact?.emails}`}>{loading?.vendor_contact?.emails}</a>
                                                </td>
                                                <td style={{width: topVendorWidths.phone}}>{loading?.vendor_contact?.phone}</td>
                                                <td style={{ width: topVendorWidths.skype }}>
                                                    {loading?.vendor_contact?.skype && (
                                                        <a href={`skype:${loading.vendor_contact.skype}?chat`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.skype}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{ width: topVendorWidths.whatsapp }}>
                                                    {loading?.vendor_contact?.whatsapp && (
                                                        <a href={`https://wa.me/${loading.vendor_contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
                                                            {loading.vendor_contact.whatsapp}
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{width: topVendorWidths.orders}}>{loading?.vendor_contact?.orders_count}</td>
                                                <td style={{width: topVendorWidths.orders}}>
                                                    {loading?.vendor_contact?.rating ?? 0}
                                                    <a
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            removeVendorContact(
                                                                loading?.vendor_contact?.id,
                                                                loading?.start_country_id,
                                                                loading?.destination_country_id
                                                            );
                                                        }}
                                                        style={{ color: 'red', textDecoration: 'none', cursor: 'pointer', fontSize: '0.8rem', marginLeft: '10px' }}
                                                    >
                                                        ✖
                                                    </a>
                                                </td>
                                                <td style={{width: '50px'}}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCheckboxes[loading?.vendor_contact?.id] || false}
                                                        onChange={() => handleCheckboxChange(loading?.vendor_contact?.id)}
                                                    />
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                    }
                </div>
            );
        }
    };

    return (
        <LoggedInView fullWidth={false}>
            {!noMeter && (
                <ul className="tabs clear" style={{width: '100%'}}>
                    {TABS.map(tab => (
                        <li key={tab.identifier} className={activeTab === tab ? "active" : ""}>
                            <a href="Javascript:;" onClick={() => handleTabClick(tab)}>{tab.name}</a>
                        </li>
                    ))}
                </ul>
            )}
            {renderTabContent()}

            <CommentModal
                open={!!commentModal}
                startCountryId={commentModal?.startCountryId ?? null}
                destinationCountryId={commentModal?.destinationCountryId ?? null}
                serviceId={serviceId}
                initialValue={commentModal?.initialValue ?? null}
                onClose={closeCommentModal}
                onSaved={() => {
                    closeCommentModal();
                    fetchData();
                }}
            />

            <p className="action" style={{display: 'flex', justifyContent: 'space-between'}}>
                <button type="button" onClick={handleReverse}><span><strong>Kontrolli vastupidist</strong></span></button>
                <button type="submit" onClick={handleSendEmail}><span><strong>Saada email</strong></span></button>
            </p>
        </LoggedInView>
    );

}